import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, CalendarDays, ShieldCheck, Loader2, PlaneTakeoff } from "lucide-react";
import axios from "axios";
import SEOHead from "../components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL;

/*  Public family-follow page — unauthenticated viewer for a shared trip
    (Phase 3 §19). Shows ONLY what the owner has approved. Never surfaces
    exact hotel address, flight numbers, costs or precise coordinates.          */
export default function FollowPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/follow/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This follow link is no longer active"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="follow-error">
      <div className="max-w-sm text-center">
        <PlaneTakeoff className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h1 className="font-serif text-xl mb-1">Link not available</h1>
        <p className="text-sm text-slate-500">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>;

  const dateFmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="follow-page">
      <SEOHead
        title={`Following ${data.title} — Family follow`}
        description={`${data.shared_by || "A traveller"} is sharing their trip. Follow only the public updates.`}
        noindex
      />
      {data.cover && (
        <div className="h-56 sm:h-72 w-full bg-slate-900">
          <img src={data.cover} alt="" className="w-full h-full object-cover opacity-90" />
        </div>
      )}
      <div className="max-w-2xl mx-auto px-5 -mt-10 relative">
        <div className="bg-white rounded-2xl shadow-xl p-5 border border-slate-200">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-violet-700 mb-1">Family follow</div>
          <h1 className="font-serif font-900 text-3xl text-slate-900">{data.title || "Trip"}</h1>
          {data.shared_by && (
            <div className="text-sm text-slate-500 mt-1">Shared by {data.shared_by}</div>
          )}
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-600">
            {(data.start_date || data.end_date) && (
              <span className="inline-flex items-center gap-1" data-testid="follow-dates">
                <CalendarDays className="w-4 h-4" />
                {dateFmt(data.start_date)}{data.end_date && ` → ${dateFmt(data.end_date)}`}
              </span>
            )}
            {data.published_stop && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full"
                data-testid="follow-published-stop">
                <MapPin className="w-3 h-3" /> Currently near {data.published_stop}
              </span>
            )}
          </div>

          {data.last_checkin && (
            <div className="mt-4 border border-slate-200 bg-slate-50 rounded-lg p-3" data-testid="follow-last-checkin">
              <div className="text-xs text-slate-500">Last check-in</div>
              <div className="font-700 text-slate-900 mt-0.5">{data.last_checkin.place}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {data.last_checkin.date}{data.last_checkin.time && ` · ${data.last_checkin.time}`}
                {data.last_checkin.mood && ` · ${data.last_checkin.mood}`}
              </div>
            </div>
          )}
        </div>

        {!!data.stops?.length && (
          <section className="mt-5 bg-white rounded-2xl p-5 border border-slate-200" data-testid="follow-stops">
            <h2 className="font-heading font-800 text-sm text-slate-500 uppercase tracking-wider mb-2">Planned stops</h2>
            <ol className="space-y-1">
              {data.stops.map((s, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-800">
                  <span className="w-5 h-5 grid place-items-center rounded-full bg-emerald-100 text-emerald-700 text-xs font-700">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
          </section>
        )}

        {!!data.timeline?.length && (
          <section className="mt-5 bg-white rounded-2xl p-5 border border-slate-200" data-testid="follow-timeline">
            <h2 className="font-heading font-800 text-sm text-slate-500 uppercase tracking-wider mb-2">Travel timeline</h2>
            <ul className="space-y-1 text-sm text-slate-700">
              {data.timeline.map((t, i) => (
                <li key={i}>
                  <span className="font-700 capitalize">{t.mode}</span>
                  {t.date && <span className="text-slate-500"> · {t.date}</span>}
                  {t.from && t.to && <span> · {t.from} → {t.to}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!data.photos?.length && (
          <section className="mt-5 bg-white rounded-2xl p-5 border border-slate-200" data-testid="follow-photos">
            <h2 className="font-heading font-800 text-sm text-slate-500 uppercase tracking-wider mb-2">Journal photos</h2>
            <div className="grid grid-cols-3 gap-1.5">
              {data.photos.map((p, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-md bg-slate-200">
                  <img src={p.thumb_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 flex items-start gap-2"
          data-testid="follow-privacy-notice">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{data.privacy_notice}</span>
        </div>
        {data.expires_at && (
          <div className="mt-2 text-xs text-slate-400 text-center">
            This follow link expires {new Date(data.expires_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}
